/**
 * Timestamp Utilities
 * ORCA360 - Timestamp normalization and formatting
 * 
 * All timestamps in the system should be stored as HH:MM:SS format
 * for consistency across the database and UI.
 */

/**
 * Normalize timestamp to HH:MM:SS format
 * Handles various input formats:
 * - MM:SS -> 00:MM:SS
 * - HH:MM:SS -> HH:MM:SS (no change)
 * - MM:SS-MM:SS -> HH:MM:SS-HH:MM:SS (ranges)
 * 
 * @param timestamp - Input timestamp in any format
 * @returns Normalized timestamp in HH:MM:SS format
 */
export function normalizeTimestamp(timestamp: string): string {
  if (!timestamp || typeof timestamp !== 'string') {
    return '00:00:00';
  }

  // Handle ranges (e.g., "5:30-7:45" or "00:05:30-00:07:45")
  if (timestamp.includes('-')) {
    const parts = timestamp.split('-').map(t => t.trim());
    const start = parts[0] || '00:00:00';
    const end = parts[1] || '00:00:00';
    return `${normalizeTimestamp(start)}-${normalizeTimestamp(end)}`;
  }

  // Remove brackets if present (e.g., "[00:05:30]" -> "00:05:30")
  const cleaned = timestamp.replace(/[\[\]]/g, '').trim();

  // Split by colon
  const parts = cleaned.split(':').map(p => parseInt(p.trim(), 10));

  // Check for invalid parts
  if (parts.some(isNaN) || parts.length < 2 || parts.length > 3) {
    console.warn(`[timestampUtils] Invalid timestamp format: "${timestamp}", returning 00:00:00`);
    return '00:00:00';
  }

  let hours: number, minutes: number, seconds: number;

  if (parts.length === 2) {
    // MM:SS format - add hours
    hours = 0;
    minutes = parts[0]!;
    seconds = parts[1]!;
  } else {
    // HH:MM:SS format - already correct
    hours = parts[0]!;
    minutes = parts[1]!;
    seconds = parts[2]!;
  }

  // Validate ranges
  if (hours < 0 || hours > 99 || minutes < 0 || minutes > 59 || seconds < 0 || seconds > 59) {
    console.warn(`[timestampUtils] Invalid time values: ${hours}:${minutes}:${seconds}, returning 00:00:00`);
    return '00:00:00';
  }

  // Format as HH:MM:SS
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
}

/**
 * Format seconds as HH:MM:SS timestamp
 * 
 * @param seconds - Time in seconds
 * @returns Formatted timestamp string
 */
export function formatSecondsToTimestamp(seconds: number): string {
  if (seconds === null || seconds === undefined || isNaN(seconds) || seconds < 0) {
    return '00:00:00';
  }

  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);

  return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
}

/**
 * Parse HH:MM:SS or MM:SS timestamp to seconds
 * 
 * @param timestamp - Timestamp string
 * @returns Total seconds
 */
export function parseTimestampToSeconds(timestamp: string): number {
  if (!timestamp || typeof timestamp !== 'string') {
    return 0;
  }

  // Remove brackets if present
  const cleaned = timestamp.replace(/[\[\]]/g, '').trim();

  // Handle ranges - use start time
  if (cleaned.includes('-')) {
    const start = cleaned.split('-')[0]?.trim();
    return start ? parseTimestampToSeconds(start) : 0;
  }

  const parts = cleaned.split(':').map(p => parseInt(p.trim(), 10));

  if (parts.some(isNaN) || parts.length < 2 || parts.length > 3) {
    return 0;
  }

  if (parts.length === 2) {
    // MM:SS
    return parts[0]! * 60 + parts[1]!;
  } else {
    // HH:MM:SS
    return parts[0]! * 3600 + parts[1]! * 60 + parts[2]!;
  }
}

/**
 * Display timestamp in user-friendly format
 * Shows MM:SS if under 1 hour, otherwise HH:MM:SS
 * 
 * @param timestamp - Timestamp in HH:MM:SS format
 * @returns Formatted display string
 */
export function displayTimestamp(timestamp: string): string {
  const normalized = normalizeTimestamp(timestamp);
  
  // Handle ranges
  if (normalized.includes('-')) {
    const parts = normalized.split('-').map(t => t.trim());
    const start = parts[0] || '00:00:00';
    const end = parts[1] || '00:00:00';
    return `${displayTimestamp(start)}-${displayTimestamp(end)}`;
  }

  const parts = normalized.split(':').map(p => parseInt(p, 10));
  if (parts.length !== 3) return normalized;

  const [hours, minutes, seconds] = parts;

  // If less than 1 hour, show MM:SS
  if (hours === 0) {
    return `${minutes}:${seconds!.toString().padStart(2, '0')}`;
  }

  // Otherwise show full HH:MM:SS
  return normalized;
}

